import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, CheckCircle, Warning, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const FACE_API_SCRIPT = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACE_MODELS = "https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights";

// Load face-api.js script once
let faceapiPromise = null;
function loadFaceApi() {
    if (faceapiPromise) return faceapiPromise;
    faceapiPromise = new Promise((resolve, reject) => {
        if (window.faceapi) return resolve(window.faceapi);
        const s = document.createElement("script");
        s.src = FACE_API_SCRIPT;
        s.async = true;
        s.onload = () => resolve(window.faceapi);
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return faceapiPromise;
}

export default function VerifyFace() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const videoRef = useRef(null);
    const [status, setStatus] = useState("loading"); // loading|ready|capturing|verifying|done|error
    const [message, setMessage] = useState("Loading face recognition models…");
    const [verified, setVerified] = useState(false);

    // 1) Load models + open webcam
    useEffect(() => {
        let stream;
        let cancelled = false;
        (async () => {
            try {
                const faceapi = await loadFaceApi();
                await Promise.all([
                    faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODELS),
                    faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODELS),
                    faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODELS),
                ]);
                if (cancelled) return;
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 } });
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                setStatus("ready");
                setMessage("Center your face and tap Verify");
            } catch (err) {
                setStatus("error");
                setMessage(err.name === "NotAllowedError"
                    ? "Camera permission denied. Allow camera access and reload."
                    : `Failed to load: ${err.message || err}`);
            }
        })();
        return () => {
            cancelled = true;
            if (stream) stream.getTracks().forEach((t) => t.stop());
        };
    }, []);

    // Check current status
    useEffect(() => {
        api.get("/users/me/voting-status").then(({ data }) => {
            if (data.eligible) {
                setVerified(true);
                setStatus("done");
                setMessage("You're verified to vote.");
            }
        }).catch(() => {});
    }, []);

    const verify = useCallback(async () => {
        if (!videoRef.current || !window.faceapi) return;
        setStatus("capturing");
        setMessage("Detecting face…");
        try {
            const faceapi = window.faceapi;
            const detection = await faceapi
                .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
                .withFaceLandmarks()
                .withFaceDescriptor();
            if (!detection || !detection.descriptor) {
                setStatus("ready");
                setMessage("No face detected — make sure your face is clearly visible and well-lit.");
                return;
            }
            setStatus("verifying");
            setMessage("Submitting verification…");
            const embedding = Array.from(detection.descriptor);
            const { data } = await api.post("/users/me/verify-face", { embedding });
            if (data.ok) {
                setVerified(true);
                setStatus("done");
                setMessage("Verified! You can now vote.");
                toast.success("Face verified — you're eligible to vote");
                await refresh();
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409) {
                setStatus("error");
                setMessage("This face is already recognized on another account. You can only vote with one account.");
            } else {
                setStatus("ready");
                setMessage(formatErr(detail) || err.message || "Verification failed");
            }
            toast.error(formatErr(detail) || "Verification failed");
        }
    }, [refresh]);

    return (
        <div className="px-4 md:px-10 py-8 max-w-2xl mx-auto">
            <button data-testid="back-btn" onClick={() => nav(-1)} className="text-wta-muted hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft size={14} /> Back
            </button>
            <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">VOTING ELIGIBILITY</div>
            <h1 className="font-heading text-5xl md:text-6xl tracking-tight uppercase">Verify Your Face</h1>
            <p className="text-wta-muted mt-2 max-w-md">
                One face per voter. Multiple accounts allowed, but only one can vote — the one whose face is registered.
            </p>

            <div className="mt-6 bg-wta-surface border border-wta-border rounded-sm overflow-hidden">
                <div className="relative w-full aspect-[3/4] md:aspect-video bg-black">
                    <video ref={videoRef} muted playsInline className="w-full h-full object-cover" data-testid="face-video" />
                    {status === "loading" && (
                        <div className="absolute inset-0 flex items-center justify-center text-wta-muted text-sm">
                            Loading models…
                        </div>
                    )}
                    {/* Center oval guide — portrait-sized on mobile */}
                    {status === "ready" && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="border-2 border-wta-primary/60 rounded-full w-64 h-80 md:w-56 md:h-72" />
                        </div>
                    )}
                    {verified && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                            <div className="text-center">
                                <CheckCircle size={64} weight="fill" className="text-wta-accent mx-auto" />
                                <div className="font-heading text-2xl tracking-wider mt-2">Verified</div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-wta-border">
                    <div className={`text-sm flex items-start gap-2 ${status === "error" ? "text-wta-primary" : "text-white"}`}>
                        {status === "error" && <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />}
                        <div>{message}</div>
                    </div>
                    <button
                        data-testid="verify-face-btn"
                        onClick={verify}
                        disabled={status === "loading" || status === "capturing" || status === "verifying" || verified}
                        className="mt-4 w-full btn-primary py-3 rounded-sm text-base disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        <Camera size={16} weight="fill" />
                        {verified ? "Already verified" : status === "verifying" || status === "capturing" ? "Working…" : "Verify"}
                    </button>
                    <div className="text-[10px] text-wta-muted mt-2 leading-snug">
                        Privacy: your photo never leaves your device — only a 128-number face signature is sent. We compare it against existing accounts and reject duplicates.
                    </div>
                </div>
            </div>
        </div>
    );
}
